import React, { useRef, useEffect, useState } from 'react';
import { makeStyles, useTheme } from '@mui/styles';
import PropTypes from 'prop-types';

import {
    zoomIdentity as D3zoomIdentity,
    select as D3select,
} from 'd3';
import D3SparklineGroup from '../../d3/D3SparklineGroup';
import useWindowSize from '../../libraries/usewindowsize';
// import { useWhatChangedEffect } from '../../libraries/withwhyrender';
import { skyBlue } from '../../data/colors';

const margins = 136;
const DEFAULTS = {
    margin: 40,
    labelWidth: 135,
    chartHeight: 40,
    scale: 'linear',
    vgrid: false,
    hgrid: true,
};

const useStyles = makeStyles(theme => ({
    root: {
        overflow: 'visible',

        '& svg': {
        },

        '& .title': {
            fill: theme.palette.text.primary,
            fillOpacity: 0.8,
            fontSize: 12,
        },

        '& .value': {
            fill: theme.palette.text.primary,
            fontSize: 12,
            fontWeight: 'bold',
        },

        '& .subtitle': {
            fill: theme.palette.text.primary,
            fillOpacity: 0.8,
            fontSize: 10,
        },

        '& .chart-background': {
            fill: theme.palette.background.paper,
            pointerEvents: 'auto',
        },

        '& .chart-background.nested': {
            fill: theme.palette.background.paper,
        },

        '& .line': {
            pointerEvents: 'none',
            fill: 'none',
            stroke: theme.ChartColors[0],
            strokeWidth: 1.5,
            strokeLinejoin: 'round',
            strokeLinecap: 'round',
        },

        '& .trendline': {
            pointerEvents: 'none',
            fill: 'none',
            stroke: theme.ChartColors[1],
            strokeDasharray: '2 8', // dash gap
            strokeWidth: 2.5,
            strokeLinejoin: 'round',
            strokeLinecap: 'round',
        },

        '& .chart': {
            overflow: 'visible',
            pointerEvents: 'none',
        },

        '& .bar': {
            fill: theme.ChartColors[0],
            pointerEvents: 'none',

            '&.bad': {
                fill: theme.palette.error.light,
            },

            '&.good': {
                fill: theme.palette.success.light,
                fillOpacity: 0.8,
            },
        },

        '& .clickable': {
            '& path:hover': {
                cursor: 'pointer',
            }
        },

        '& .expanded': {
            '& .line': {
                stroke: skyBlue[400],
            },
            '& .linechart': {
                color: skyBlue[400],
            },

            '& .linechartarea': {
                color: skyBlue[400],
            },
        },

        '& .chart .bar.highlight': {
            fill: theme.palette.info.main,
        },

        '& .tip': {
            overflow: 'visible',
        },

        '& .tip .tip-rect': {
            fillOpacity: 0.7,
        },

        '& .tip .tip-pointer': {
            pointerEvents: 'none',
            fill: theme.palette.primary.contrastText,
        },

        '& .tip .tip-time': {
            font: theme.typography.tooltips.font,
            pointerEvents: 'none',
            fill: 'black',
        },

        '& .tip .tip-div': {
            font: theme.typography.tooltips.font,
            pointerEvents: 'none',
            fill: 'black',
        },

        '& .tip .tip-val': {
            font: theme.typography.tooltips.font,
            pointerEvents: 'none',
            fill: 'black',
        },

        '& .tip .tip-val.bad': {
            fill: 'black', // theme.palette.error.light,
        },

        '& .tip .tip-val.good': {
            fill: 'black', // theme.palette.success.light,
        },

        '& .linechart': {
            strokeWidth: 1.5,
            fill: 'none',
            pointerEvents: 'none',
            // this is intentionally not an svg path attribute
            // it will be picked up programmatically and applied if no override was provided.
            // some svg attributes (fill, stroke) cannot be overridden in code
            color: theme.ChartColors[0],
        },

        '& .linechartarea': {
            fillOpacity: 0.2,
            // pointerEvents: 'none',
            // see note on .linechart
            color: theme.ChartColors[0],
        },

        '& .timeline-axis .tick line': {
            pointerEvents: 'none',
        },

        '& .hgrid line, .vgrid line': {
            opacity: 0.2,
            strokeWidth: 1,
        },

        '& .tip .tip-text': {
            font: theme.typography.tooltips.font,
            pointerEvents: 'none',
            fill: theme.ChartColors[0],
        },

        '& .tip-date': {
            fill: theme.palette.text.primary,
            fontSize: 12, // override tip size for dates
            font: theme.typography.tooltips.font,
        },

        '& .tip .tip-text.bad': {
            fill: theme.palette.error.light,
        },

        '& .tip .tip-text.good': {
            fill: theme.palette.success.light,
        },

        '& .group-hover': {
            stroke: theme.palette.primary.main,
        },

        '& .hover-line': {
            stroke: theme.palette.background.paper,
            strokeWidth: 0.5,
            opacity: 0,
            pointerEvents: 'none'
        },

        '& path.handle:focus': {
            outline: 'none',
        },

        // pass theme colors to buttons
        '& .zbtn': {
            fillOpacity: 0,
            stokeOpacity: 0,

            '&:hover': {
                fill: theme.palette.primary.light,
                fillOpacity: 0.1,
            },

            '& path': {
                fill: theme.palette.primary.light,
                fillOpacity: 1,

                '&.disabled': {
                    fill: theme.palette.text.primary,
                    fillOpacity: 0.3,
                }
            }
        },
    },
    fabContainer: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        height: 60,
        width: '100%',
    },
    slider: {
        width: '40%',
        float: 'right',
        paddingTop: 25,
    }
}));

const SparklineGroup = (props) => {
    const d3Container = useRef(null);
    const classes = useStyles();
    const theme = useTheme();
    const { width } = useWindowSize();
    const [cwidth, setCWidth] = useState((props.width || 436) - margins);
    const [txfm, setTxfm] = useState({ txfm: D3zoomIdentity });

    useEffect(() => {
        if (props.width) {
            setCWidth(props.width);
        }
    }, [props.width]);

    useEffect(() => {
        if (props.width) return;
        setCWidth(Math.max((width || 436) - margins, 300));
    }, [width, props.width]);

    useEffect(() => {
        if (d3Container.current) {
            const node = d3Container.current;
            const settings = { ...props.settings, DEFAULTS };
            const slider = 30;

            const svgs = D3select(node)
                .data(['once'])
                .join('div')
                .selectAll('svg.SparklineGroup')
                .data(props.metrics, function (d) { return d.key })
                .join('svg')
                .attr('viewbox', '0 0 100 100')
                .attr('height', d => {
                    const expand = (props.expand && props.expand.key === d.key)
                        ? settings.expandHeight : 0;
                    return props.height + 40 + slider + expand;
                })
                .attr('width', cwidth)
                .attr('class', d => `SparklineGroup ${d.key}`);

            const daGraph = D3SparklineGroup()
                .theme(theme)
                .settings(settings)
                .colorScale(props.colors)
                .width(cwidth)
                .start(props.start)
                .end(props.end)
                .height(props.height)
                .expand(props.expand)
                .txfm(d => {
                    if (d.key === txfm.key) return txfm.txfm;
                    return D3zoomIdentity;
                })
                .on('click', props.onClick)
                .on('zoom', setTxfm);
            svgs.call(daGraph);
        }
    }, [
        cwidth,
        txfm,
        props.onClick,
        props.colors,
        theme,
        props.height,
        props.metrics,
        props.end,
        props.start,
        props.settings,
        props.expand,
    ]);

    return (
        <div>
            <div className={classes.root} ref={d3Container}>
            </div>
        </div>
    );
};

SparklineGroup.propTypes = {
    metrics: PropTypes.array.isRequired,
    height: PropTypes.number.isRequired,
    width: PropTypes.number,
    start: PropTypes.string.isRequired,
    end: PropTypes.string.isRequired,
    settings: PropTypes.object,
    onClick: PropTypes.func,
    colors: PropTypes.func,
    expand: PropTypes.object,
};

export default SparklineGroup;
